import { useMemo, useState } from 'react';
import { Input, Select } from '@ds';
import { IOption, SelectChangeVariant } from '@ds/elements/Select/Select.types';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import { useGetReferenceData } from 'src/shared/queries/useGetReferenceData';
import { useGetStates } from 'src/shared/queries/useGetStates';
import { ContactAddress } from 'src/shared/types/User';
import { InputChangeVariant } from '@design-system/elements/Input/Input.types';

interface IAddress {
  refAddress: ContactAddress;
  onFieldChange: (fieldName: string, value: string) => void;
  onOptionChange: (fieldName: string, selectedOption: IOption) => void;
  disabled?: boolean;
}

const Address = ({ refAddress, onFieldChange, onOptionChange, disabled = false }: IAddress) => {
  const [address, setAddress] = useState<ContactAddress>(refAddress);
  const { data: addressTypesData } = useGetReferenceData('CONTACT_ADDRESS_TYPE');
  const { data: countriesData } = useGetCountries();
  const { data: statesData } = useGetStates(address?.country || '');

  const countryOptions = useMemo(
    () => countriesData?.map(({ countryCode, countryName }) => ({ value: countryCode, label: countryName })) || [],
    [countriesData]
  );

  const stateOptions = useMemo(
    () => statesData?.map(({ stateName }) => ({ value: stateName, label: stateName })) || [],
    [statesData]
  );

  const addressTypesOptions = useMemo(
    () => addressTypesData?.map(({ code, description }) => ({ value: code, label: description })) || [],
    [addressTypesData]
  );

  const hasChanged = (field: keyof ContactAddress) =>
    refAddress ? (address[field] || '') !== (refAddress[field] || '') : false;

  const handleOnOptionChange = (fieldName: string, data: IOption | null) => {
    setAddress((prev) => ({ ...prev, [fieldName]: data?.value || '' }));
    onOptionChange(fieldName, data as IOption);
  };

  const handleOnFieldChange = (fieldName: string, value: string) => {
    setAddress((prev) => ({ ...prev, [fieldName]: value }));
    onFieldChange(fieldName, value);
  };

  const styles = {
    container: 'w-full w-full flex flex-col gap-[16px] lg:gap-[40px]',
    row: 'flex flex-col lg:flex-row gap-[16px] lg:gap-[24px]',
    col3: 'w-full lg:w-1/4',
    col6: 'w-full lg:w-1/2',
  };

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <div className={`flex flex-col lg:flex-row gap-[16px] lg:gap-[24px] xl:pr-[16px] w-full xl:w-1/2`}>
          <div className={styles.col6}>
            <Select
              onChange={(data) => handleOnOptionChange('addressType', data as IOption)}
              options={addressTypesOptions}
              label="Type"
              selectedOption={{ value: address.addressType || '', label: '' }}
              showClear={!!address.addressType}
              hasChanged={hasChanged('addressType')}
              disabled={disabled}
              selectChangedStyle={SelectChangeVariant.Secondary}
            />
          </div>
          <div className={styles.col6}>
            <Select
              onChange={(data) => {
                handleOnOptionChange('country', data as IOption);
                setAddress((prev) => ({ ...prev, state: '' }));
              }}
              options={countryOptions}
              label="Country / Location"
              selectedOption={{ value: address.country as string, label: address.country as string }}
              searchable
              showClear={!!address.country}
              hasChanged={hasChanged('country')}
              disabled={disabled}
              selectChangedStyle={SelectChangeVariant.Secondary}
            />
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.col6}>
          <Input
            label="Address Line 1"
            value={address.addressLine1}
            onChange={(e) => handleOnFieldChange('addressLine1', e.target.value)}
            hasChanged={hasChanged('addressLine1')}
            disabled={disabled}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col6}>
          <Input
            label="Address Line 2"
            value={address.addressLine2}
            onChange={(e) => handleOnFieldChange('addressLine2', e.target.value)}
            hasChanged={hasChanged('addressLine2')}
            disabled={disabled}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.col3}>
          <Input
            label="City"
            value={address.city}
            onChange={(e) => handleOnFieldChange('city', e.target.value)}
            hasChanged={hasChanged('city')}
            disabled={disabled}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col3}>
          <Input
            label="Floor / Apt / Suite"
            value={address.suite}
            onChange={(e) => handleOnFieldChange('suite', e.target.value)}
            hasChanged={hasChanged('suite')}
            disabled={disabled}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col3}>
          {stateOptions.length > 0 ? (
            <Select
              onChange={(data) => handleOnOptionChange('state', data as IOption)}
              options={stateOptions}
              label="State / Province"
              selectedOption={{ value: address.state as string, label: '' }}
              searchable
              showClear={!!address.state}
              hasChanged={hasChanged('state')}
              disabled={disabled}
              selectChangedStyle={SelectChangeVariant.Secondary}
            />
          ) : (
            <Input
              label="State / Province"
              value={address.state}
              onChange={(e) => handleOnFieldChange('state', e.target.value)}
              hasChanged={hasChanged('state')}
              disabled={disabled}
              inputChangedStyle={InputChangeVariant.Secondary}
            />
          )}
        </div>
        <div className={styles.col3}>
          <Input
            label="Zip / Postal Code"
            value={address.zipCode}
            onChange={(e) => handleOnFieldChange('zipCode', e.target.value)}
            hasChanged={hasChanged('zipCode')}
            disabled={disabled}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
      </div>
    </div>
  );
};

export default Address;
